import React from "react";
import { borderClassGradient, customShadow } from "./utils/classes";
import {
  UseFormRegister,
  FieldError,
  FieldValues,
  Path,
} from "react-hook-form";

interface FormInputProps<TFormValues extends FieldValues> {
  label: string;
  name: Path<TFormValues>;
  type?: string;
  placeholder: string;
  register?: UseFormRegister<TFormValues>;
  requiredMessage?: string;
  error?: FieldError;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  minLength?: number;
  maxLength?: number;
}

const isValidEmail = (email: string | undefined): boolean => {
  if (!email) return false;
  const simpleEmailRegex =
    /^[A-Za-z0-9._-]+@[A-Za-z0-9._-]+(\.[A-Za-z0-9._-]+)+$/;
  return simpleEmailRegex.test(email);
};

export function FormInput<TFormValues extends FieldValues>({
  label,
  name,
  type = "text",
  placeholder,
  register,
  requiredMessage,
  error,
  value,
  onChange,
  minLength = 1,
  maxLength = 100,
}: FormInputProps<TFormValues>) {
  const isControlled = value !== undefined && onChange !== undefined;
  const isEmailField = type === "email";

  const registerProps =
    !isControlled && register
      ? register(name, {
          required: requiredMessage || undefined,
          ...(isEmailField
            ? {
                maxLength: {
                  value: maxLength,
                  message: `Maximum ${maxLength} characters allowed.`,
                },
              }
            : {
                minLength: {
                  value: minLength,
                  message: `Minimum ${minLength} character${
                    minLength > 1 ? "s" : ""
                  } required.`,
                },
                maxLength: {
                  value: maxLength,
                  message: `Maximum ${maxLength} characters allowed.`,
                },
              }),
          validate: isEmailField
            ? {
                validEmail: (value: string) =>
                  isValidEmail(value) || "Please enter a valid email address.",
              }
            : {
                notOnlyWhitespace: (value: string) =>
                  value.trim().length > 0 ||
                  "Field cannot be empty or contain only spaces.",
              },
        })
      : {};

  return (
    <div className="flex flex-col relative max-lg:w-[84.0vw]">
      <label
        htmlFor={name}
        className="text-black/40 p-[0.4vw] max-lg:p-[1vw] leading-[110%] font-luxora-regular text-left text-[1.11vw] max-lg:text-[4.2vw]"
      >
        {label}
      </label>

      <div
        className={`relative ${borderClassGradient} rounded-[1.25vw] max-lg:!rounded-[3.2vw]`}
      >
        <input
          id={name}
          type={type}
          placeholder={placeholder}
          className={`
          bg-[#F3F3F8] placeholder:text-black/25 font-luxora-regular text-[1.38vw]
          h-[4vw] placeholder:text-[1.38vw] placeholder:leading-[110%]
          p-[1.67vw] rounded-[1.25vw] placeholder:font-luxora-regular
          focus:outline-none w-full
          text-black ${customShadow}
         


          max-lg:h-[13.3vw]
          max-lg:p-[3vw] 
          max-lg:!rounded-[3.2vw]
          leading-[110%]
          max-lg:placeholder:text-[4.2vw] 
          max-lg:text-[4.2vw] 
        `}
          {...(isControlled ? { value, onChange } : registerProps)}
        />
      </div>

      {error && (
        <p
          className="text-red-500 absolute top-[6.3vw] max-lg:ml-[1vw] max-lg:top-[20.5vw] text-left
         ml-[0.5vw] leading-[100%] text-[1.1vw] max-lg:text-[3vw] "
        >
          {error.message}
        </p>
      )}
    </div>
  );
}
